import { Mark, Node, ResolvedPos, Slice } from 'prosemirror-model'
import { PluginKey, TextSelection, type Transaction } from 'prosemirror-state'
import type { DecorationSet, EditorView } from 'prosemirror-view'

import { constants, schema } from '@showrunner/prose-schemas'

import { TrackedChangeType } from '#components/ChangeTracking/types'
import { uuid } from '#util'

import { isInTitlePage } from '../titlePageView'

const { TRACKED_ADDITION, TRACKED_DELETION } = constants.MarkTypeMap

/**
 * Used as a mapping of tracked changes ids to their position in the document
 */
export interface TrackedChangeInfo {
  id: string
  changeType: TrackedChangeType
  snippet: string
  from: number
  to: number
}
export interface MarkChangesState {
  selectedId?: string
  trackedChanges: Map<string, TrackedChangeInfo[]>
  decorations: DecorationSet
  localChanges: Set<string>
}

export const markChangesKey = new PluginKey<MarkChangesState>('mark-changes')

const getProposedMark = (
  pos: ResolvedPos,
  markType: string,
): Mark | undefined =>
  pos
    .marksAcross(pos)
    ?.find((m) => m.type.name === markType && m.attrs.status === 'proposed')

const getAdditionMark = (pos?: ResolvedPos): Mark | undefined => {
  if (!pos) return
  return getProposedMark(pos, TRACKED_ADDITION)
}

const getDeletionMark = (pos: ResolvedPos): Mark | undefined =>
  getProposedMark(pos, TRACKED_DELETION)

export const isAddition = (mark: Mark) => mark.type.name === TRACKED_ADDITION
const isDeletion = (mark: Mark) => mark.type.name === TRACKED_DELETION
export const isTracked = (mark: Mark) => isAddition(mark) || isDeletion(mark)

const isApproved = (mark: Mark) => mark.attrs.status === 'approved'
export const isProposed = (mark: Mark) => mark.attrs.status === 'proposed'
const isRejected = (mark: Mark) => mark.attrs.status === 'rejected'
export const isRejectedAddition = (m: Mark) => isAddition(m) && isRejected(m)
export const isApprovedDeletion = (m: Mark) => isDeletion(m) && isApproved(m)
export const isUnrejectedDeletion = (m: Mark) => isDeletion(m) && !isRejected(m)

export const createAdditionMark = ($pos?: ResolvedPos): Mark => {
  const id = getAdditionMark($pos)?.attrs.id ?? uuid()
  return schema.marks.trackedAddition.create({ id })
}

export const createDeletionMark = ($pos: ResolvedPos): Mark => {
  const id = getDeletionMark($pos)?.attrs.id ?? uuid()
  return schema.marks.trackedDeletion.create({ id })
}

const hasProposedMark = (
  doc: Node,
  from: number,
  to: number,
  markTypeName: string,
): boolean => {
  let found = false
  if (to > from) {
    doc.nodesBetween(from, to, (node) => {
      if (
        node.marks.some(
          (m) => m.type.name === markTypeName && m.attrs.status === 'proposed',
        )
      ) {
        found = true
      }
      return !found
    })
  }
  return found
}

export const hasPendingAdditionMark = (
  doc: Node,
  from: number,
  to: number,
): boolean => hasProposedMark(doc, from, to, TRACKED_ADDITION)

// technically the mark could be non-contiguous, but i dont
// think that scenario is problematic for our purposes
export const rangeIsSameAddition = (
  start: ResolvedPos,
  end: ResolvedPos,
): boolean => {
  const startMarkId = getAdditionMark(start)?.attrs.id
  const endMarkId = getAdditionMark(end)?.attrs.id
  return startMarkId && endMarkId && startMarkId === endMarkId
}

export const nudgeSelection = (tr: Transaction, dir: number) => {
  const sel = TextSelection.findFrom(
    tr.doc.resolve(tr.selection.$anchor.pos + dir),
    1,
    true,
  )
  if (sel) tr.setSelection(sel)
  return tr.scrollIntoView()
}

// when folks type 'on top' of existing content, we mark the selection
// as a proposed deletion, nudge their cursor and afterward inject the
// new text with a mark of its own
export const handleTextInput = (
  view: EditorView,
  from: number,
  to: number,
  text: string,
): boolean => {
  // plain cursors dont require special handling
  if (to - from === 0) return false

  if (isInTitlePage(from, view.state)) {
    return false
  }

  const { tr } = view.state
  const { $from } = tr.selection
  const end = tr.doc.resolve(to - 1)
  // if the entire range is within the same proposed addition, delete normally
  if (rangeIsSameAddition($from, end)) return false

  const deletionMark = createDeletionMark($from)
  tr.addMark(from, to, deletionMark)

  const sel = TextSelection.findFrom(tr.selection.$to, 0, true)
  if (sel) tr.setSelection(sel)

  const additionMark = createAdditionMark()
  tr.setStoredMarks([additionMark])
  tr.insertText(text)

  // we track the deletion mark as locally created via meta
  // because the selection no longer touches it when it is applied
  const meta = { type: 'local-changes', id: deletionMark.attrs.id }
  tr.setMeta(markChangesKey, meta)

  view.dispatch(tr.scrollIntoView())
  return true
}

export const handlePaste = (
  view: EditorView,
  _: ClipboardEvent,
  slice: Slice,
): boolean => {
  // we dont allow paste 'on top' of existing content (yet)
  if (!view.state.selection.empty) return true

  if (isInTitlePage(view.state.selection.from, view.state)) {
    return false
  }

  const { tr } = view.state
  const start = tr.doc.resolve(tr.selection.$from.pos - 1)
  tr.replaceSelection(slice)

  const fragSize = tr.mapping.map(tr.selection.from) - tr.selection.from

  tr.addMark(
    tr.selection.from - fragSize,
    tr.selection.from,
    createAdditionMark(start),
  )
    .scrollIntoView()
    .setMeta('paste', true)
    .setMeta('uiEvent', 'paste')

  view.dispatch(tr)
  return true
}
